import { useMemo, useRef, useState } from "react";
import type {
  CrawlBatchResponse,
  CrawlInitResponse,
  CrawlPageResult,
  ExistingSitemap,
  RobotsSnapshot
} from "../shared/types";
import { buildCsv, buildIndexNowPayload, buildSitemapXml } from "../shared/sitemap";
import { canonicalizeUrl, isSameOrigin, safeHostname } from "../shared/url";
import { postJson } from "./lib/api";
import { downloadText } from "./lib/download";
import { buildJsonReport, buildSitemapEntries, countBySeverity, flattenIssues } from "./lib/report";
import { summarizeRobots } from "./lib/robotsSummary";
import type { RobotsUiSummary } from "./lib/robotsSummary";

const DEFAULT_MAX_URLS = 50_000;
const BATCH_SIZE = 200;

type RunStatus = "idle" | "running" | "done" | "stopped" | "error";
type Tab = "urls" | "issues" | "xml" | "indexnow";
type DisplayIssue = ReturnType<typeof flattenIssues>[number];

interface IssueSummaryItem {
  code: string;
  severity: "error" | "warning";
  label: string;
  count: number;
  message: string;
  exampleUrl: string;
}

interface GuidanceInfo {
  tone: "idle" | "running" | "ok" | "warning" | "error";
  title: string;
  summary: string;
  items: string[];
  cta?: {
    label: string;
    tab: Tab;
  };
}

interface CrawlState {
  status: RunStatus;
  inputUrl: string;
  origin: string;
  host: string;
  robots?: RobotsSnapshot;
  existingSitemaps: ExistingSitemap[];
  pages: CrawlPageResult[];
  visitedCount: number;
  queuedCount: number;
  discoveredCount: number;
  message: string;
}

const initialState: CrawlState = {
  status: "idle",
  inputUrl: "",
  origin: "",
  host: "",
  existingSitemaps: [],
  pages: [],
  visitedCount: 0,
  queuedCount: 0,
  discoveredCount: 0,
  message: "待機中です。"
};

export default function App() {
  const [url, setUrl] = useState("");
  const [maxUrls, setMaxUrls] = useState(DEFAULT_MAX_URLS);
  const [state, setState] = useState<CrawlState>(initialState);
  const [activeTab, setActiveTab] = useState<Tab>("urls");
  const [indexNowKey, setIndexNowKey] = useState("");
  const [indexNowKeyLocation, setIndexNowKeyLocation] = useState("");
  const stopRef = useRef(false);

  const sitemapEntries = useMemo(() => buildSitemapEntries(state.pages, state.origin), [state.pages, state.origin]);
  const issues = useMemo(() => flattenIssues(state.pages), [state.pages]);
  const severityCounts = useMemo(() => countBySeverity(issues), [issues]);
  const actionableIssues = useMemo(() => issues.filter((issue) => issue.severity !== "info"), [issues]);
  const issueSummaries = useMemo(() => summarizeActionableIssues(actionableIssues), [actionableIssues]);
  const robotsSummary = useMemo(() => summarizeRobots(state.robots), [state.robots]);
  const guidance = useMemo(
    () =>
      buildGuidance({
        status: state.status,
        sitemapCount: sitemapEntries.length,
        errorCount: severityCounts.error,
        warningCount: severityCounts.warning,
        infoCount: severityCounts.info,
        issueSummaries,
        robotsSummary,
        existingSitemapCount: state.existingSitemaps.length
      }),
    [
      state.status,
      sitemapEntries.length,
      severityCounts.error,
      severityCounts.warning,
      severityCounts.info,
      issueSummaries,
      robotsSummary,
      state.existingSitemaps.length
    ]
  );
  const sitemapXml = useMemo(() => buildSitemapXml(sitemapEntries), [sitemapEntries]);
  const csv = useMemo(() => buildCsv(sitemapEntries), [sitemapEntries]);
  const jsonReport = useMemo(() => buildJsonReport(state.pages, sitemapEntries, issues), [state.pages, sitemapEntries, issues]);
  const indexNowPayload = useMemo(
    () => buildIndexNowPayload(state.host, sitemapEntries.map((entry) => entry.loc), indexNowKey, indexNowKeyLocation),
    [state.host, sitemapEntries, indexNowKey, indexNowKeyLocation]
  );

  async function startCrawl() {
    stopRef.current = false;
    setActiveTab("urls");
    setState({
      ...initialState,
      status: "running",
      inputUrl: url,
      message: "初期URLとrobots.txtを確認しています。"
    });

    try {
      const init = await postJson<CrawlInitResponse>("/api/crawl/init", { url, maxUrls });
      const queue: string[] = [];
      const queued = new Set<string>();
      const visited = new Set<string>();
      const pages: CrawlPageResult[] = [];

      const enqueue = (candidate: string) => {
        if (queued.size >= maxUrls) {
          return;
        }
        const normalized = canonicalizeUrl(candidate);
        if (!isSameOrigin(normalized, init.origin) || queued.has(normalized) || visited.has(normalized)) {
          return;
        }
        queued.add(normalized);
        queue.push(normalized);
      };

      init.seedUrls.forEach(enqueue);

      setState((current) => ({
        ...current,
        origin: init.origin,
        host: init.host,
        robots: init.robots,
        existingSitemaps: init.existingSitemaps,
        queuedCount: queue.length,
        discoveredCount: queued.size,
        message: `${safeHostname(init.rootUrl)} をクロールしています。`
      }));

      while (queue.length > 0 && visited.size < maxUrls && !stopRef.current) {
        const batch = queue.splice(0, BATCH_SIZE).filter((item) => !visited.has(item)).slice(0, BATCH_SIZE);
        if (batch.length === 0) {
          continue;
        }

        batch.forEach((item) => visited.add(item));
        const response = await postJson<CrawlBatchResponse>("/api/crawl/batch", {
          origin: init.origin,
          urls: batch,
          robotsText: init.robots.body
        });

        pages.push(...response.results);

        for (const page of response.results) {
          for (const discovered of page.discoveredUrls) {
            enqueue(discovered);
          }
        }

        setState((current) => ({
          ...current,
          pages: [...pages],
          visitedCount: visited.size,
          queuedCount: queue.length,
          discoveredCount: queued.size,
          message: stopRef.current ? "停止処理中です。" : `${visited.size.toLocaleString()} URLを確認しました。`
        }));

        await delay(80);
      }

      setState((current) => ({
        ...current,
        status: stopRef.current ? "stopped" : "done",
        pages: [...pages],
        visitedCount: visited.size,
        queuedCount: queue.length,
        discoveredCount: queued.size,
        message: stopRef.current ? "クロールを停止しました。" : "クロールが完了しました。"
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        status: "error",
        message: error instanceof Error ? error.message : "エラーが発生しました。"
      }));
    }
  }

  function stopCrawl() {
    stopRef.current = true;
    setState((current) => ({ ...current, message: "停止リクエストを受け付けました。" }));
  }

  const running = state.status === "running";
  const canDownload = sitemapEntries.length > 0;
  const progress = state.discoveredCount > 0 ? Math.min(100, Math.round((state.visitedCount / state.discoveredCount) * 100)) : 0;

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <p className="eyebrow">Cloudflare Pages + Workers</p>
          <h1>sitemap.xml Maker</h1>
        </div>
        <div className={`status-pill status-${state.status}`}>{statusLabel(state.status)}</div>
      </header>

      <section className="workspace">
        <aside className="panel control-panel">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!running) {
                void startCrawl();
              }
            }}
          >
            <label className="field">
              <span>対象URL</span>
              <input
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://example.com/"
                disabled={running}
                inputMode="url"
              />
            </label>

            <label className="field">
              <span>上限URL数</span>
              <input
                value={maxUrls}
                onChange={(event) => setMaxUrls(clamp(Number(event.target.value) || DEFAULT_MAX_URLS, 100, DEFAULT_MAX_URLS))}
                type="number"
                min={100}
                max={DEFAULT_MAX_URLS}
                step={100}
                disabled={running}
              />
            </label>

            <div className="button-row">
              <button className="primary-button" type="submit" disabled={running || !url.trim()}>
                クロール開始
              </button>
              <button className="secondary-button" type="button" onClick={stopCrawl} disabled={!running}>
                停止
              </button>
            </div>
          </form>

          <div className="progress-block">
            <div className="progress-header">
              <span>{state.message}</span>
              <strong>{progress}%</strong>
            </div>
            <div className="progress-track" aria-label="クロール進捗">
              <div className="progress-value" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <div className={`mini-map mini-map-${state.status}`} aria-label="クロール状況の可視化">
            {Array.from({ length: 48 }, (_, index) => {
              const completed = state.status === "done";
              const active = completed || (state.discoveredCount > 0 && index / 48 < progress / 100);
              return (
                <span
                  key={index}
                  className={active ? "active" : ""}
                  style={{ transitionDelay: completed ? `${index * 12}ms` : "0ms" }}
                />
              );
            })}
          </div>
        </aside>

        <section className="main-column">
          <section className="metrics-grid" aria-label="クロール集計">
            <Metric label="発見URL" value={state.discoveredCount} />
            <Metric label="確認済み" value={state.visitedCount} />
            <Metric label="XML対象" value={sitemapEntries.length} />
            <Metric
              label="要確認"
              value={severityCounts.error + severityCounts.warning}
              tone="alert"
              detail={`${severityCounts.error.toLocaleString()} Error / ${severityCounts.warning.toLocaleString()} Warning`}
            />
          </section>

          <section className="panel diagnostics-panel">
            <div className="panel-heading">
              <div>
                <p className="eyebrow">SEO / AIO 基礎診断</p>
                <h2>{state.host || "未実行"}</h2>
              </div>
              <div className="download-actions">
                <button
                  className="download-primary"
                  disabled={!canDownload}
                  onClick={() => downloadText("sitemap.xml", "application/xml", sitemapXml)}
                >
                  <span className="download-mark" aria-hidden="true">↓</span>
                  <span className="download-copy">
                    <strong>sitemap.xmlをダウンロード</strong>
                    <small>{canDownload ? `${sitemapEntries.length.toLocaleString()} URL` : "未生成"}</small>
                  </span>
                </button>
                <div className="download-row download-secondary">
                  <button disabled={!canDownload} onClick={() => downloadText("sitemap.csv", "text/csv", csv)}>
                    CSV保存
                  </button>
                  <button disabled={!state.pages.length} onClick={() => downloadText("crawl-report.json", "application/json", jsonReport)}>
                    JSON保存
                  </button>
                </div>
              </div>
            </div>

            <div className="diagnostic-strip">
              <Diagnostic label="robots.txt" value={state.robots ? `${state.robots.status || "取得不可"}` : "-"} />
              <Diagnostic label="既存sitemap" value={state.existingSitemaps.length} />
              <Diagnostic label="Error" value={severityCounts.error} tone="error" />
              <Diagnostic label="Warning" value={severityCounts.warning} tone="warning" />
              <Diagnostic label="Info" value={severityCounts.info} />
            </div>

            <GuidancePanel guidance={guidance} onSelectTab={setActiveTab} />

            {state.robots && robotsSummary && <RobotsPanel robots={state.robots} summary={robotsSummary} />}

            {actionableIssues.length > 0 && (
              <section className="issue-summary" aria-label="要確認の内訳">
                <div className="issue-summary-head">
                  <div>
                    <strong>要確認の内訳</strong>
                    <span>XML対象から外すべきURLや、検索エンジン向けに確認したいURLです。</span>
                  </div>
                  <button type="button" onClick={() => setActiveTab("issues")}>
                    Issuesを見る
                  </button>
                </div>
                <div className="issue-summary-list">
                  {issueSummaries.slice(0, 4).map((summary) => (
                    <article key={`${summary.severity}-${summary.code}`} className={`issue-summary-item issue-summary-${summary.severity}`}>
                      <div>
                        <strong>{summary.label}</strong>
                        <span>{summary.count.toLocaleString()} URL</span>
                      </div>
                      <p>{summary.message}</p>
                      <small>{shorten(summary.exampleUrl, state.origin)}</small>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {state.existingSitemaps.length > 0 && (
              <div className="sitemap-hints">
                {state.existingSitemaps.map((sitemap) => (
                  <span key={sitemap.url}>{sitemap.urlCount.toLocaleString()} URLs: {sitemap.url}</span>
                ))}
              </div>
            )}
          </section>

          <section className="panel results-panel">
            <nav className="tabs" aria-label="結果表示">
              <TabButton active={activeTab === "urls"} onClick={() => setActiveTab("urls")}>URLs</TabButton>
              <TabButton active={activeTab === "issues"} onClick={() => setActiveTab("issues")}>Issues</TabButton>
              <TabButton active={activeTab === "xml"} onClick={() => setActiveTab("xml")}>XML</TabButton>
              <TabButton active={activeTab === "indexnow"} onClick={() => setActiveTab("indexnow")}>IndexNow</TabButton>
            </nav>

            {activeTab === "urls" && <UrlsTable pages={state.pages} origin={state.origin} />}
            {activeTab === "issues" && <IssuesTable issues={issues} />}
            {activeTab === "xml" && <CodePanel value={sitemapXml} />}
            {activeTab === "indexnow" && (
              <div className="indexnow-panel">
                <div className="indexnow-controls">
                  <label className="field">
                    <span>IndexNow key</span>
                    <input value={indexNowKey} onChange={(event) => setIndexNowKey(event.target.value)} placeholder="your-key" />
                  </label>
                  <label className="field">
                    <span>keyLocation</span>
                    <input
                      value={indexNowKeyLocation}
                      onChange={(event) => setIndexNowKeyLocation(event.target.value)}
                      placeholder="https://example.com/key.txt"
                    />
                  </label>
                  <button onClick={() => downloadText("indexnow-payload.json", "application/json", indexNowPayload)} disabled={!canDownload}>
                    JSON保存
                  </button>
                </div>
                <CodePanel value={indexNowPayload} />
              </div>
            )}
          </section>
        </section>
      </section>
    </main>
  );
}

function Metric({ label, value, tone = "default", detail }: { label: string; value: number; tone?: "default" | "alert"; detail?: string }) {
  return (
    <div className={`metric metric-${tone}`}>
      <span>{label}</span>
      <strong>{value.toLocaleString()}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function Diagnostic({ label, value, tone = "default" }: { label: string; value: string | number; tone?: "default" | "error" | "warning" }) {
  return (
    <div className={`diagnostic diagnostic-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function GuidancePanel({ guidance, onSelectTab }: { guidance: GuidanceInfo; onSelectTab: (tab: Tab) => void }) {
  return (
    <section className={`guidance-panel guidance-${guidance.tone}`} aria-label="診断結果の読み方">
      <div className="guidance-copy">
        <strong>{guidance.title}</strong>
        <p>{guidance.summary}</p>
      </div>
      <ul>
        {guidance.items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {guidance.cta && (
        <button type="button" onClick={() => onSelectTab(guidance.cta!.tab)}>
          {guidance.cta.label}
        </button>
      )}
    </section>
  );
}

function RobotsPanel({ robots, summary }: { robots: RobotsSnapshot; summary: RobotsUiSummary }) {
  return (
    <section className="robots-panel" aria-label="robots.txtの内容">
      <div className="robots-head">
        <div>
          <strong>robots.txtの内容</strong>
          <span>{robots.url}</span>
        </div>
        <a href={robots.url} target="_blank" rel="noreferrer">
          開く
        </a>
      </div>

      <div className="robots-stat-grid">
        <RobotStat label="Status" value={summary.statusLabel} />
        <RobotStat label="User-agent" value={summary.userAgents.length} />
        <RobotStat label="Allow" value={summary.allowRules.length} />
        <RobotStat label="Disallow" value={summary.disallowRules.length} tone={summary.disallowRules.length ? "warning" : "default"} />
        <RobotStat label="Sitemap" value={summary.sitemapUrls.length} />
      </div>

      {!summary.hasBody && (
        <p className="robots-empty">
          robots.txtの本文は空です。検索エンジンにはクロール制限なしとして扱われます。
        </p>
      )}

      {summary.disallowRules.length > 0 && (
        <div className="robots-rule-block">
          <strong>Disallow</strong>
          <div className="robots-rule-list">
            {summary.disallowRules.slice(0, 6).map((rule) => (
              <code key={rule}>{rule}</code>
            ))}
            {summary.disallowRules.length > 6 && <span>ほか {summary.disallowRules.length - 6} 件</span>}
          </div>
        </div>
      )}

      {summary.sitemapUrls.length > 0 && (
        <div className="robots-rule-block">
          <strong>Sitemap</strong>
          <div className="robots-rule-list">
            {summary.sitemapUrls.slice(0, 3).map((sitemapUrl) => (
              <code key={sitemapUrl}>{sitemapUrl}</code>
            ))}
            {summary.sitemapUrls.length > 3 && <span>ほか {summary.sitemapUrls.length - 3} 件</span>}
          </div>
        </div>
      )}

      {summary.hasBody && (
        <details className="robots-raw">
          <summary>全文を見る</summary>
          <textarea value={robots.body} readOnly spellCheck={false} />
        </details>
      )}
    </section>
  );
}

function RobotStat({
  label,
  value,
  tone = "default"
}: {
  label: string;
  value: string | number;
  tone?: "default" | "warning";
}) {
  return (
    <div className={`robot-stat robot-stat-${tone}`}>
      <span>{label}</span>
      <strong>{typeof value === "number" ? value.toLocaleString() : value}</strong>
    </div>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button className={active ? "active" : ""} type="button" onClick={onClick}>
      {children}
    </button>
  );
}

function UrlsTable({ pages, origin }: { pages: CrawlPageResult[]; origin: string }) {
  if (!pages.length) {
    return <EmptyState text="まだURLはありません。" />;
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Status</th>
            <th>URL</th>
            <th>Title</th>
            <th>Links</th>
            <th>Schema</th>
          </tr>
        </thead>
        <tbody>
          {pages.slice(0, 500).map((page) => (
            <tr key={`${page.requestedUrl}-${page.status}`}>
              <td><span className={`code status-code ${page.ok ? "ok" : "bad"}`}>{page.blockedByRobots ? "robots" : page.status || "ERR"}</span></td>
              <td>
                <a href={page.finalUrl} target="_blank" rel="noreferrer">{shorten(page.finalUrl || page.requestedUrl, origin)}</a>
                {page.canonicalUrl && page.canonicalUrl !== page.finalUrl && <small>canonical: {shorten(page.canonicalUrl, origin)}</small>}
              </td>
              <td>{page.title ?? "-"}</td>
              <td>{page.discoveredUrls.length}</td>
              <td>{page.hasStructuredData ? "あり" : "未検出"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function IssuesTable({ issues }: { issues: ReturnType<typeof flattenIssues> }) {
  if (!issues.length) {
    return <EmptyState text="問題はまだ検出されていません。" />;
  }
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Severity</th>
            <th>Code</th>
            <th>URL</th>
            <th>Message</th>
          </tr>
        </thead>
        <tbody>
          {issues.slice(0, 500).map((issue, index) => (
            <tr key={`${issue.url}-${issue.code}-${index}`}>
              <td><span className={`severity severity-${issue.severity}`}>{issue.severity}</span></td>
              <td>{issue.code}</td>
              <td><a href={issue.url} target="_blank" rel="noreferrer">{issue.url}</a></td>
              <td>{issue.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodePanel({ value }: { value: string }) {
  return <textarea className="code-panel" value={value} readOnly spellCheck={false} />;
}

function EmptyState({ text }: { text: string }) {
  return <div className="empty-state">{text}</div>;
}

function statusLabel(status: RunStatus): string {
  switch (status) {
    case "running":
      return "Running";
    case "done":
      return "Done";
    case "stopped":
      return "Stopped";
    case "error":
      return "Error";
    default:
      return "Ready";
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function shorten(url: string, origin: string): string {
  if (origin && url.startsWith(origin)) {
    return url.replace(origin, "") || "/";
  }
  return url;
}

function summarizeActionableIssues(issues: DisplayIssue[]): IssueSummaryItem[] {
  const grouped = new Map<string, IssueSummaryItem>();
  for (const issue of issues) {
    if (issue.severity === "info") {
      continue;
    }
    const key = `${issue.severity}:${issue.code}`;
    const current = grouped.get(key);
    if (current) {
      current.count += 1;
      continue;
    }
    grouped.set(key, {
      code: issue.code,
      severity: issue.severity,
      label: issueLabel(issue.code),
      count: 1,
      message: issue.message,
      exampleUrl: issue.url
    });
  }
  return [...grouped.values()].sort((a, b) => severityRank(a.severity) - severityRank(b.severity) || b.count - a.count);
}

function issueLabel(code: string): string {
  const labels: Record<string, string> = {
    robots_blocked: "robots.txtでブロック",
    noindex: "noindex指定",
    canonical_external: "外部canonical",
    canonical_mismatch: "canonical不一致",
    http_error: "HTTPエラー",
    fetch_failed: "取得失敗",
    js_heavy: "JavaScript依存",
    nofollow: "nofollow指定"
  };
  return labels[code] ?? code;
}

function severityRank(severity: "error" | "warning"): number {
  return severity === "error" ? 0 : 1;
}

function buildGuidance({
  status,
  sitemapCount,
  errorCount,
  warningCount,
  infoCount,
  issueSummaries,
  robotsSummary,
  existingSitemapCount
}: {
  status: RunStatus;
  sitemapCount: number;
  errorCount: number;
  warningCount: number;
  infoCount: number;
  issueSummaries: IssueSummaryItem[];
  robotsSummary: RobotsUiSummary | undefined;
  existingSitemapCount: number;
}): GuidanceInfo {
  const actionableCount = errorCount + warningCount;

  if (status === "running") {
    return {
      tone: "running",
      title: "いま起きていること",
      summary: "robots.txtを確認しながら、同一ホスト内のリンクを小分けに取得しています。",
      items: [
        "robots.txtで禁止されているURLはXML対象から外します。",
        "noindex、外部canonical、HTTPエラーは要確認として集計します。",
        "途中で止めても、取得済みURLからXML/CSV/JSONを保存できます。"
      ]
    };
  }

  if (status === "error") {
    return {
      tone: "error",
      title: "処理が止まっています",
      summary: "入力URL、対象サイトの応答、robots.txtの取得状況を確認してください。",
      items: [
        "URLは https:// から始まる実在ページを指定します。",
        "対象サイトが一時的に遅い場合は、少し待って再実行します。",
        "JSON保存ができる場合は、失敗URLとエラー内容を確認します。"
      ]
    };
  }

  if (status === "stopped") {
    return {
      tone: "warning",
      title: "途中停止しています",
      summary: "ここまで取得できたURLだけでXMLを作れます。全体を見たい場合は再実行してください。",
      items: [
        "XML対象数が想定より少ない場合は、上限URL数を増やして再クロールします。",
        "Issuesタブで途中までに見つかった除外理由を確認できます。",
        "既存sitemapが検出されていれば、seedとして再利用されます。"
      ],
      cta: { label: "Issuesを見る", tab: "issues" }
    };
  }

  if (status === "done" && actionableCount > 0) {
    const issueItems = issueSummaries.slice(0, 3).map((summary) => `${summary.label}: ${summary.count.toLocaleString()} URL。${issueAction(summary.code)}`);
    return {
      tone: errorCount > 0 ? "error" : "warning",
      title: `${errorCount.toLocaleString()} Error / ${warningCount.toLocaleString()} Warning`,
      summary: "XMLに入れない方がよいURL、または検索エンジン向けに確認したいURLがあります。",
      items: [
        ...issueItems,
        "代表URLと詳細はIssuesタブで確認できます。修正後に再クロールするとXML対象が変わります。"
      ],
      cta: { label: "Issuesを見る", tab: "issues" }
    };
  }

  if (status === "done") {
    const sitemapText = sitemapCount > 0
      ? `${sitemapCount.toLocaleString()} URLをsitemap.xml候補として出力できます。`
      : "XML対象URLがありません。robots.txt、noindex、HTTPエラー、開始URLを確認してください。";
    const robotsText = robotsSummary?.disallowRules.length
      ? `robots.txtにはDisallowが${robotsSummary.disallowRules.length.toLocaleString()}件あります。意図した除外か確認します。`
      : "robots.txtで大きな除外ルールは見つかっていません。";
    return {
      tone: sitemapCount > 0 ? "ok" : "warning",
      title: "大きな要確認はありません",
      summary: `Error / Warningは0件です。${sitemapText}`,
      items: [
        robotsText,
        existingSitemapCount > 0 ? "既存sitemapも検出済みです。新旧のURL数や除外方針に差がないか確認します。" : "既存sitemapがない場合は、生成したsitemap.xmlをサイトのルートへ設置します。",
        "設置後はrobots.txtに Sitemap: の行を追加するか、Google Search Consoleへ送信します。",
        infoCount > 0 ? `Infoが${infoCount.toLocaleString()}件あります。必須修正ではありませんが、構造化データや内部リンク改善の候補です。` : "Infoも0件なら、まずはXMLを保存して送信用の準備に進めます。"
      ],
      cta: sitemapCount > 0 ? { label: "XMLを見る", tab: "xml" } : undefined
    };
  }

  return {
    tone: "idle",
    title: "まだ診断前です",
    summary: "URLを入力してクロールすると、sitemap.xml候補とSEO/AIO向けの確認ポイントをまとめます。",
    items: [
      "robots.txt、既存sitemap、canonical、noindex、HTTPステータスを確認します。",
      "Error / Warningは、XMLに入れる前に確認したい項目です。",
      "Infoは改善候補です。構造化データや内部リンクの不足を見つけるために使います。"
    ]
  };
}

function issueAction(code: string): string {
  const actions: Record<string, string> = {
    robots_blocked: "robots.txtで除外したいURLならOK。入れたいURLならDisallowを見直します。",
    noindex: "検索に出したいページならmeta robotsをindex許可にします。",
    canonical_external: "sitemapには自サイト内の正規URLだけを入れるのが安全です。",
    canonical_mismatch: "canonical先をsitemapに入れるか、canonical設定を合わせます。",
    http_error: "リンク切れ、削除済み、サーバーエラーを確認します。",
    fetch_failed: "対象URLの応答、SSL、アクセス制限を確認します。",
    js_heavy: "HTML上に内部リンクや本文が見えるか確認します。",
    nofollow: "内部リンク発見を弱める意図があるか確認します。"
  };
  return actions[code] ?? "詳細を確認し、XMLに含めるべきURLか判断します。";
}
